/** Complimentary cart SKUs only; this module never delivers PDFs or creates gift-card credit. */

const normalizeId = (value) => String(value ?? '');

/** Includes reading/parsing the response body; a timed-out mutation is never replayed. */
export const runCartRequest = async (operation, timeoutMs = 20000) => {
  const controller = new AbortController();
  let timer;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      const error = new Error('The cart update took too long. Check your cart before trying again.');
      error.code = 'CART_REQUEST_TIMEOUT';
      reject(error);
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([Promise.resolve().then(() => operation(controller.signal)), timeout]);
  } finally {
    clearTimeout(timer);
  }
};

export const isGiftConfigEnabled = (config) => {
  const ebookId = normalizeId(config?.variantId);
  const balmId = normalizeId(config?.balmVariantId);
  const giftCardId = normalizeId(config?.giftCardVariantId);
  const validId = (id) => /^[1-9]\d*$/.test(id);
  return config?.enabled === true && validId(ebookId) && validId(balmId) && ebookId !== balmId &&
    (!giftCardId || (validId(giftCardId) && giftCardId !== ebookId && giftCardId !== balmId));
};

const isOneTimeItem = (item) => !item.selling_plan_allocation && !item.selling_plan;
const isFullCartSnapshot = (cart) => Array.isArray(cart?.items) &&
  Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 &&
  cart.items.every((item) => Number.isSafeInteger(item.quantity) && item.quantity > 0) &&
  cart.items.reduce((quantity, item) => quantity + item.quantity, 0) === cart.item_count;

/** Initial-cart rewards only; one-time gift lines are never added to a recurring plan. */
export const planEbookGift = (cart, config) => {
  if (!isGiftConfigEnabled(config)) {
    return { enabled: false, balmQuantity: 0, desiredQuantity: 0, desiredGiftCardQuantity: 0, updates: {}, missingItems: [], needsAdd: false };
  }
  const items = Array.isArray(cart?.items) ? cart.items : [];
  // Both purchase types qualify on this checkout, including split discount allocations.
  // Reward payloads below omit selling_plan; only the Balm participates in recurring delivery.
  const balmQuantity = items
    .filter((item) => normalizeId(item.variant_id ?? item.id) === normalizeId(config.balmVariantId))
    .reduce((quantity, item) => {
      const count = Number(item.quantity);
      return quantity + (Number.isSafeInteger(count) && count > 0 ? count : 0);
    }, 0);
  const desiredQuantity = balmQuantity >= 3 ? 1 : 0;
  const giftCardId = normalizeId(config.giftCardVariantId);
  const desiredGiftCardQuantity = giftCardId && balmQuantity >= 6 ? 1 : 0;
  const updates = {};
  const missingItems = [];
  const rewards = [
    { id: normalizeId(config.variantId), quantity: desiredQuantity, kind: 'ultimate-leg-care' },
    ...(giftCardId ? [{ id: giftCardId, quantity: desiredGiftCardQuantity, kind: 'gift-card-20-entitlement' }] : []),
  ];
  rewards.forEach((reward) => {
    const gifts = items.filter((item) => normalizeId(item.variant_id ?? item.id) === reward.id);
    const retained = reward.quantity ? gifts.find(isOneTimeItem) : null;
    gifts.forEach((item) => {
      const quantity = item === retained ? reward.quantity : 0;
      if (Number(item.quantity) !== quantity) {
        if (typeof item.key !== 'string' || !item.key) throw new Error('Your cart changed. Please refresh it before confirming your bonus items.');
        updates[item.key] = quantity;
      }
    });
    if (reward.quantity && !retained) {
      missingItems.push({ id: reward.id, quantity: 1, properties: { _skin_elite_bonus: reward.kind } });
    }
  });
  return { enabled: true, balmQuantity, desiredQuantity, desiredGiftCardQuantity, updates, missingItems, needsAdd: missingItems.length > 0 };
};

/** Serializes the original mutation and its gift reconciliation as one operation. */
export const createMutationQueue = () => {
  let tail = Promise.resolve();
  let pending = 0;
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener(pending));
  return {
    get pending() { return pending; },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    run(operation) {
      pending += 1;
      notify();
      const result = tail.then(operation);
      tail = result.catch(() => {}).finally(() => {
        pending -= 1;
        notify();
      });
      return result;
    },
    async idle() {
      let observed;
      do {
        observed = tail;
        await observed;
      } while (observed !== tail);
    },
  };
};

/** Dependency-injected Ajax service so timeout/duplicate behavior can be tested in Node. */
export const createGiftCartService = ({ fetchImpl, readConfig, rootPath = () => '/', getSectionIds = () => [], requestTimeoutMs = 20000, queue = createMutationQueue() }) => {
  const route = (name) => `${rootPath()}cart/${name}.js`;
  const sectionOptions = (options) => ({
    sections: [...new Set(options?.sectionIds ?? getSectionIds())].filter(Boolean).slice(0, 5).join(','),
    sections_url: options?.sectionsUrl ?? (typeof window === 'undefined' ? '/' : window.location.pathname),
  });
  const request = (url, body) => runCartRequest(async (signal) => {
    const response = await fetchImpl(url, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      signal,
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const payload = await response.json();
    if (!response.ok || payload?.errors || Number(payload?.status) >= 400) {
      throw new Error(payload?.description || payload?.message || 'Your free bonus items could not be confirmed. Please try again.');
    }
    return payload;
  }, requestTimeoutMs);
  const fetchCart = async () => {
    const cart = await request(`${rootPath()}cart.js`);
    if (!Array.isArray(cart?.items)) throw new Error('Your cart could not be confirmed. Please try again.');
    return cart;
  };
  const reconcile = async (previousCart, options = {}) => {
    const config = readConfig();
    if (!isGiftConfigEnabled(config)) return previousCart;
    // A full Ajax mutation response already contains fresh discount-adjusted keys.
    // /add.js can return only the added lines, so it still requires a cart read.
    let finalCart = isFullCartSnapshot(previousCart) ? previousCart : await fetchCart();
    let finalSections = previousCart?.sections;
    let changed = false;
    const attemptedAdds = new Set();
    // Removals and missing rewards may coexist. Re-plan from fresh keys after each mutation.
    for (let pass = 0; pass < 4; pass += 1) {
      const plan = planEbookGift(finalCart, config);
      if (Object.keys(plan.updates).length) {
        const updated = await request(route('update'), { updates: plan.updates, ...sectionOptions(options) });
        if (!Array.isArray(updated?.items)) throw new Error('Your free bonus items could not be confirmed. Please try again.');
        finalSections = updated.sections;
        finalCart = updated;
      } else if (plan.needsAdd) {
        // A success response without the expected line is uncertain, just like a timeout.
        // Do not replay that SKU's POST within this operation; the next attempt first reads cart.
        if (plan.missingItems.some((item) => attemptedAdds.has(item.id))) break;
        plan.missingItems.forEach((item) => attemptedAdds.add(item.id));
        const added = await request(route('add'), { items: plan.missingItems, ...sectionOptions(options) });
        finalSections = added?.sections;
        finalCart = added;
      } else {
        break;
      }
      changed = true;
      if (!isFullCartSnapshot(finalCart)) finalCart = await fetchCart();
    }
    const verification = planEbookGift(finalCart, config);
    if (verification.needsAdd || Object.keys(verification.updates).length) {
      throw new Error('Your free bonus items could not be confirmed. Please try again.');
    }
    const expectedIds = [
      ...(verification.desiredQuantity ? [normalizeId(config.variantId)] : []),
      ...(verification.desiredGiftCardQuantity ? [normalizeId(config.giftCardVariantId)] : []),
    ];
    for (const item of finalCart.items) {
      if (expectedIds.includes(normalizeId(item.variant_id ?? item.id)) && Object.hasOwn(item, 'final_line_price') &&
          (!Number.isSafeInteger(item.final_line_price) || item.final_line_price !== 0)) {
        throw new Error('Your bonus items could not be confirmed as free. Please review your cart.');
      }
    }
    return {
      ...finalCart,
      sections: finalSections,
      ebookGiftChanged: changed,
    };
  };
  return {
    queue, fetchCart,
    snapshot(options = {}) {
      // UI refresh is read-only and stays behind pending cart mutations.
      return queue.run(async () => {
        const cart = await fetchCart();
        const sectionIds = [...new Set(options.sectionIds || [])].filter((id) => id === 'cart-drawer-section');
        const sections = {};
        if (sectionIds.length) {
          const payload = await request(`${rootPath()}?sections=${encodeURIComponent(sectionIds.join(','))}`);
          for (const id of sectionIds) {
            if (typeof payload?.[id] === 'string' && payload[id].trim()) sections[id] = payload[id];
          }
        }
        // Never reuse mutation/page-cache HTML when the fresh section is missing or null.
        return { ...cart, sections, ebookGiftChanged: false };
      });
    },
    run(operation, options) {
      return queue.run(async () => {
        const result = await operation();
        // Preserve the native cart component's inline validation/error path.
        if (result?.errors || Number(result?.status) >= 400) return result;
        return reconcile(result, options);
      });
    },
    sync(options = {}) {
      return queue.run(async () => {
        if (!isGiftConfigEnabled(readConfig())) return null;
        let previousCart = null;
        // Checkout can preserve an unsubmitted note without submitting stale updates[] indices.
        if (typeof options.note === 'string') {
          const current = await fetchCart();
          previousCart = options.note === String(current.note ?? '') ? current :
            await request(route('update'), { note: options.note, ...sectionOptions(options) });
        }
        return reconcile(previousCart, options);
      });
    },
  };
};

const readBrowserConfig = () => {
  const data = typeof document === 'undefined' ? null : document.querySelector('[data-skin-elite-ebook-config]')?.dataset;
  return data ? {
    enabled: data.enabled === 'true', variantId: data.variantId,
    giftCardVariantId: data.giftCardVariantId, balmVariantId: data.balmVariantId,
  } : { enabled: false };
};
const browserSectionIds = () => typeof document === 'undefined' ? [] :
  [...new Set([...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
const createBrowserService = (queue) => createGiftCartService({
  fetchImpl: (...args) => globalThis.fetch(...args), readConfig: readBrowserConfig,
  rootPath: () => globalThis.Shopify?.routes?.root || '/', getSectionIds: browserSectionIds, queue,
});
// asset_url carries ?v= while relative imports do not: both module URLs must share one queue.
const browserService = typeof window === 'undefined' ? createBrowserService() :
  (window.__skinEliteEbookGiftService ||= createBrowserService());

export const runCartMutation = (operation, options) => browserService.run(operation, options);
export const fetchCartState = () => browserService.fetchCart();
export const isEbookGiftEnabled = () => isGiftConfigEnabled(readBrowserConfig());

const installBrowserGuards = async () => {
  if (!isGiftConfigEnabled(readBrowserConfig()) || window.__skinEliteEbookGiftInstalled) return;
  window.__skinEliteEbookGiftInstalled = true;
  const externalOperations = new Set();
  const savedDisabled = new WeakMap();
  let checkoutAttempt = false;
  const updateCheckoutState = () => {
    const busy = browserService.queue.pending > 0 || externalOperations.size > 0 || checkoutAttempt;
    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      if (busy) {
        if (!savedDisabled.has(button)) savedDisabled.set(button, button.disabled);
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else if (savedDisabled.has(button)) {
        button.disabled = savedDisabled.get(button);
        savedDisabled.delete(button);
        button.removeAttribute('aria-busy');
      }
    });
  };
  const showError = () => {
    document.querySelectorAll('.cart__ctas').forEach((container) => {
      let status = container.querySelector('[data-skin-elite-ebook-status]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.skinEliteEbookStatus = '';
        status.setAttribute('role', 'alert');
        status.style.cssText = 'margin:0 0 8px;color:#a33025;font-size:13px;line-height:1.4';
        container.prepend(status);
      }
      status.textContent = 'We could not confirm your free bonus items. Please try checkout again.';
    });
  };
  browserService.queue.subscribe(updateCheckoutState);
  new MutationObserver(updateCheckoutState).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('submit', async (event) => {
    const originalForm = event.target;
    if (!(originalForm instanceof HTMLFormElement) || !originalForm.matches('form.cart-form') ||
        event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutAttempt) return;
    checkoutAttempt = true;
    updateCheckoutState();
    const noteField = originalForm.elements.namedItem('note');
    const note = typeof noteField?.value === 'string' ? noteField.value : undefined;
    try {
      while (externalOperations.size) await Promise.all([...externalOperations]);
      await browserService.sync({ note });
      // Never POST the old form: gift insertion can shift its updates[] line indices.
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (error) {
      console.warn('[skin-elite-ebook] Checkout reconciliation failed:', error);
      showError();
    } finally {
      checkoutAttempt = false;
      updateCheckoutState();
    }
  }, true);

  const { CartLinesUpdateEvent, StandardEvents } = await import('@shopify/events');
  const emitFinalCart = (cart, force = false) => {
    if (!cart || (!force && !cart.ebookGiftChanged)) return;
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart', lines: [],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'skin-elite-ebook-gift', didError: false },
      }),
    }));
  };
  const ownSources = new Set([
    'skin-elite-miracle-balm', 'skin-elite-cart-bundle-groups', 'cart-items-component',
    'skin-elite-ebook-gift', 'skin-elite-bamboo-gift', 'compression-bundle',
  ]);
  const onExternalUpdate = (event) => {
    if (!event.promise) return;
    const pending = runCartRequest(() => Promise.resolve(event.promise))
      .then(async (result) => {
        if (ownSources.has(result?.detail?.source) || result?.detail?.didError) return;
        try {
          emitFinalCart(await browserService.sync());
        } catch (error) {
          showError();
          throw error;
        }
      })
      .catch((error) => {
        console.warn('[skin-elite-ebook] External cart update failed:', error);
      })
      .finally(() => {
        externalOperations.delete(pending);
        updateCheckoutState();
      });
    externalOperations.add(pending);
    updateCheckoutState();
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, onExternalUpdate);
  document.addEventListener(StandardEvents.cartDiscountUpdate, onExternalUpdate);
  document.addEventListener(StandardEvents.cartNoteUpdate, onExternalUpdate);
  const syncCartSnapshot = async () => {
    try {
      // This is background guard work, not a top-level module await: native cart can import us.
      if (document.querySelector('cart-items-component')) await customElements.whenDefined('cart-items-component');
      // A cached relative import may have created the shared service before snapshot existed.
      const reader = typeof browserService.snapshot === 'function' ? browserService : createBrowserService(browserService.queue);
      const cart = await reader.snapshot({ sectionIds: browserSectionIds() });
      emitFinalCart(cart, true);
    } catch (error) {
      console.warn('[skin-elite-ebook] Cart snapshot reconciliation deferred:', error);
    }
  };
  // History can restore a stale drawer even when the server already has the correct rewards.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) return syncCartSnapshot();
  });
  // Preserve initial gift reconciliation; history UI refresh itself performs GETs only.
  browserService.sync().then(syncCartSnapshot).catch((error) => {
    console.warn('[skin-elite-ebook] Initial reconciliation deferred:', error);
  });
};

if (typeof document !== 'undefined') {
  const install = () => installBrowserGuards().catch((error) => console.warn('[skin-elite-ebook] Setup failed:', error));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
  else install();
}
